import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Shield, Lock, Eye, EyeOff, Users, Server, Ban, Handshake, ChevronLeft, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" as const },
  }),
};

const sections = [
  {
    icon: Lock,
    title: "You Own 100% of Your Data",
    description: "Everything you create inside SentinelForge—proposals, contracts, pricing logic, threat assessments, templates, client details—belongs entirely to you. We do not claim ownership of any content you generate.",
    points: [
      "Your proposals stay private",
      "Your pricing stays private",
      "Your templates stay private",
      "Your client information stays private",
    ],
    footer: "No one outside your account can access your materials.",
  },
  {
    icon: EyeOff,
    title: "We Cannot See Your Proposals or Pricing",
    description: "Your documents are stored in isolated, private data containers. SentinelForge staff cannot view, access, or use your proposals, pricing, or templates unless you explicitly grant temporary support access.",
    points: [
      "We do not read your proposals",
      "We do not review your pricing",
      "We do not analyze your templates",
      "We do not use your content to train AI",
      "We do not use your content to build new templates",
      "We do not share your content with anyone",
    ],
    footer: "Your competitive edge stays yours.",
  },
  {
    icon: Ban,
    title: "We Do Not Compete With You",
    description: "SentinelForge is a software platform—not a security company, not a PI firm, not a cleaning company, not a contractor.",
    points: [
      "We do not bid on contracts",
      "We do not provide field services",
      "We do not sell leads",
      "We do not share your information with competitors",
    ],
    footer: "Our only mission is to help you win more business, faster.",
  },
  {
    icon: Handshake,
    title: "Your Data Is Never Sold or Shared",
    description: "We do not sell, rent, trade, or share your information with third parties. We do not use your proposals or pricing to create industry averages or benchmarks. We do not use your content for marketing or analytics.",
    points: [],
    footer: "Your business stays your business.",
  },
  {
    icon: Server,
    title: "Secure Storage & Encrypted Communication",
    description: "All data is encrypted in transit and at rest. All communication between your browser and our platform is secured with industry-standard encryption.",
    points: [],
    footer: "Your information is protected at every step.",
  },
  {
    icon: Users,
    title: "Your Team Controls Access",
    description: "Only users you authorize can view or edit your proposals, contracts, or threat assessments. You can remove access at any time.",
    points: [],
    footer: "You stay in full control.",
  },
  {
    icon: Eye,
    title: "No Hidden Access. No Backdoors. No Surprises.",
    description: "We believe in transparency. We do not access your account without permission. We do not monitor your activity. We do not use your data for internal research or development.",
    points: [],
    footer: "Your trust is the foundation of our platform.",
  },
];

const DataSafety = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-7 w-7 text-primary" />
            <span className="text-lg font-bold text-foreground tracking-tight">Sentinel Forge</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/data-safety" className="text-foreground font-medium">Your Data Is Safe</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
            <Link to="/demo" className="hover:text-foreground transition-colors">Demo</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Sign In</Button>
            </Link>
            <Link to="/auth?signup">
              <Button size="sm" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-44 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-dark" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-success/5 blur-3xl" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
              <ChevronLeft className="h-4 w-4" /> Back to Home
            </Link>

            <div className="mx-auto mb-6 h-16 w-16 rounded-2xl bg-success/10 flex items-center justify-center">
              <Shield className="h-8 w-8 text-success" />
            </div>

            <h1 className="text-4xl md:text-5xl font-extrabold text-foreground leading-[1.1] mb-6">
              Your Data Is{" "}
              <span className="text-success">Safe</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Your Data. Your Clients. Your Competitive Edge. Always Protected.
            </p>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-muted-foreground text-base md:text-lg leading-relaxed text-center"
          >
            SentinelForge was built for service companies that rely on trust, confidentiality, and competitive advantage. Whether you're in security, private investigation, cleaning, construction, fire watch, or any other service industry, your proposals and pricing are the core of your business. We treat them that way.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-muted-foreground text-base md:text-lg leading-relaxed text-center mt-4"
          >
            This page explains exactly how your information is protected and why your data is never shared, sold, or used against you.
          </motion.p>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto px-6 space-y-12">
          {sections.map((section, i) => (
            <motion.div
              key={section.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp}
              className="border border-border rounded-xl bg-card p-8 md:p-10"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <section.icon className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground leading-tight pt-1">{section.title}</h2>
              </div>

              <p className="text-muted-foreground leading-relaxed mb-4">{section.description}</p>

              {section.points.length > 0 && (
                <ul className="space-y-2 mb-4">
                  {section.points.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-secondary-foreground text-sm">
                      <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
              )}

              <p className="text-foreground font-medium text-sm border-t border-border pt-4 mt-4">{section.footer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="border border-primary/30 rounded-xl bg-primary/5 p-8 md:p-10 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              A Platform Built for Trust‑Critical Industries
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto mb-6">
              Security companies, PI firms, and other service providers operate in environments where confidentiality is everything. SentinelForge was built with that reality in mind.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm font-medium text-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-success" /> Your data is protected.</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-success" /> Your clients are protected.</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-success" /> Your competitive advantage is protected.</span>
            </div>
            <div className="mt-8">
              <Link to="/auth?signup">
                <Button className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
                  Get Started — It's Free
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/50 py-10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/data-safety" className="text-foreground font-medium">Data Safety</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataSafety;
